/**
 * useAuth — application auth hook, Clerk edition.
 *
 * Composes Clerk's auth state with the local account store so the rest of the
 * app has a single, stable surface for auth-related concerns:
 *
 *   isLoaded          — Clerk has finished initialising (safe to render gates)
 *   isAuthenticated   — user is signed in to Clerk
 *   profileComplete   — post-signup onboarding has been completed
 *   user              — local user profile (from our DB, loaded via /auth/me)
 *   clerkUser         — raw Clerk User object (for MFA management, etc.)
 *   activeAccount     — currently selected cellar account
 *   accounts          — all cellar accounts the user belongs to
 *   canEdit / canAdmin — role-based capability flags for the active account
 *   logout            — signs out of Clerk and clears local state
 *   refreshAccounts   — re-fetches account list from the API
 *   setActiveAccount  — switch the active cellar
 *   updateUser        — optimistically patch the local user in the store
 */
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth as useClerkAuth, useUser, useSession } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAccountStore } from '../stores/account.store';
import { authApi, setClerkTokenGetter, resetAuthSuccessState } from '../lib/api';
export function useAuth() {
    const { isSignedIn, isLoaded, signOut } = useClerkAuth();
    const { user: clerkUser } = useUser();
    const { session } = useSession();
    const store = useAccountStore();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // ── Logout ────────────────────────────────────────────────────────────────
    const logout = useCallback(async () => {
        resetAuthSuccessState();
        store.clearAuth();
        queryClient.clear();
        await signOut();
        navigate('/login');
    }, [store, queryClient, signOut, navigate]);
    // Listen for 401s dispatched by api.ts (session truly expired server-side)
    useEffect(() => {
        const handler = () => logout();
        window.addEventListener('auth:expired', handler);
        return () => window.removeEventListener('auth:expired', handler);
    }, [logout]);
    // ── Load local user + accounts on sign-in ─────────────────────────────────
    useEffect(() => {
        if (!isLoaded || !isSignedIn || !session)
            return;
        // Guarantee the token getter is registered in the same synchronous tick as
        // the API call below, regardless of whether ClerkBridge's own effect has
        // already run.  Both ClerkBridge and this hook set the same module-level
        // getter, so whichever runs last wins — with identical session values the
        // result is always correct.
        setClerkTokenGetter(() => session.getToken());
        authApi.me()
            .then((data) => {
            store.setUser({
                id: data.id,
                email: data.email,
                username: data.username,
                firstName: data.firstName,
                lastName: data.lastName,
                isPlatformAdmin: data.isPlatformAdmin,
            });
            store.setProfileComplete(data.profileComplete);
            if (data.accounts.length > 0) {
                const mapped = data.accounts.map((a) => ({
                    id: a.id,
                    name: a.name,
                    slug: a.slug,
                    role: a.role,
                    organisationId: a.organisationId ?? null,
                    isArchived: a.isArchived ?? false,
                    archivedAt: a.archivedAt ?? null,
                }));
                store.setAccounts(mapped);
                // Preserve current active account if it still exists; otherwise use first
                const still = store.activeAccount
                    ? mapped.find((a) => a.id === store.activeAccount.id)
                    : undefined;
                store.setActiveAccount(still ?? mapped[0]);
            }
        })
            .catch(() => {
            // /me failed — likely a network blip or the profile is incomplete.
            // The AppLayout will redirect to /onboarding if needed.
        });
        // session is included so the effect re-runs when the Clerk session object
        // becomes available (it can be null briefly on first load, even when
        // isSignedIn is already true).  Store refs are stable; no need to list them.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, isSignedIn, session]);
    // ── Clear local state on sign-out ─────────────────────────────────────────
    useEffect(() => {
        if (isLoaded && !isSignedIn) {
            store.clearAuth();
            queryClient.clear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, isSignedIn]);
    // ── Account management ─────────────────────────────────────────────────────
    const refreshAccounts = useCallback(async () => {
        const data = await authApi.me();
        const mapped = data.accounts.map((a) => ({
            id: a.id,
            name: a.name,
            slug: a.slug,
            role: a.role,
            organisationId: a.organisationId ?? null,
            isArchived: a.isArchived ?? false,
            archivedAt: a.archivedAt ?? null,
        }));
        store.setAccounts(mapped);
        store.setProfileComplete(data.profileComplete);
        const active = store.activeAccount;
        if (active) {
            const updated = mapped.find((a) => a.id === active.id);
            if (updated)
                store.setActiveAccount(updated);
        }
    }, [store]);
    // ── Derived flags ─────────────────────────────────────────────────────────
    const isAuthenticated = Boolean(isLoaded && isSignedIn);
    const canEdit = store.activeAccount?.role
        ? ['owner', 'admin', 'editor'].includes(store.activeAccount.role)
        : false;
    const canAdmin = store.activeAccount?.role
        ? ['owner', 'admin'].includes(store.activeAccount.role)
        : false;
    return {
        /** True once Clerk has resolved the session (safe to render protected routes). */
        isLoaded,
        isAuthenticated,
        profileComplete: store.profileComplete,
        user: store.user,
        /** Raw Clerk User object — use for MFA setup, avatar, email management, etc. */
        clerkUser,
        activeAccount: store.activeAccount,
        accounts: store.accounts,
        canEdit,
        canAdmin,
        logout,
        refreshAccounts,
        setActiveAccount: store.setActiveAccount,
        updateUser: store.updateUser,
    };
}
